import { useState } from 'react';
import { apiFetch } from '../../utils/api';

// token      — shared-link token from the URL (used in the PATCH route, not as Bearer)
// photo      — { _id, credit: { _id, approvalStatus } }
// onDecision — callback(photoId, newApprovalStatus) called after successful PATCH
export default function ApproveRejectButtons({ photo, token, onDecision }) {
  const [pending, setPending] = useState(null); // 'approved' | 'rejected' | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentStatus = photo.credit?.approvalStatus;
  const isApproved = currentStatus === 'approved';
  const isRejected = currentStatus === 'rejected';

  const handleDecision = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/photos/${photo.id}/status/${token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvalStatus: pending }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Request failed');
      }
      onDecision(photo.id, pending);
      setPending(null);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const btnBase = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: 4,
    fontSize: 16,
    fontWeight: 600,
    minWidth: 120,
    minHeight: 44,
    color: 'white',
  };

  // Confirmation step
  if (pending) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <p style={{ color: 'white', margin: 0, fontSize: 15 }}>
          {pending === 'approved' ? 'Approve this photo? (This cannot be undone)' : 'Reject this photo?'}
        </p>
        {error && <p style={{ color: '#f87171', margin: 0, fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={handleDecision}
            disabled={loading}
            style={{
              ...btnBase,
              background: pending === 'approved' ? '#1A5C25' : '#7B1C1C',
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Saving…' : 'Confirm'}
          </button>
          <button
            onClick={() => { setPending(null); setError(null); }}
            disabled={loading}
            style={{ ...btnBase, background: '#444', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Main approve / reject buttons
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          disabled={isApproved}
          onClick={() => setPending('approved')}
          style={{
            ...btnBase,
            background: isApproved ? '#1A5C25' : '#2a2a2a',
            outline: isApproved ? '2px solid #4ade80' : 'none',
            cursor: isApproved ? 'not-allowed' : 'pointer',
          }}
        >
          {isApproved ? '✓ Approved' : 'Approve'}
        </button>
        {!isApproved && (
          <button
            onClick={() => !isRejected && setPending('rejected')}
            style={{
              ...btnBase,
              background: isRejected ? '#7B1C1C' : '#2a2a2a',
              outline: isRejected ? '2px solid #f87171' : 'none',
              cursor: isRejected ? 'default' : 'pointer',
            }}
          >
            {isRejected ? '✕ Rejected' : 'Reject'}
          </button>
        )}
      </div>
      {isApproved && (
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, margin: 0, textAlign: 'center' }}>
          Approval is final and cannot be changed.
        </p>
      )}
    </div>
  );
}